import { createContext, useContext, useState, ReactNode } from 'react';
import { en } from '../locales/en';
import { es } from '../locales/es';
import { it } from '../locales/it';

type Translations = typeof en;
type LanguageCode = 'en' | 'es' | 'it';

type LanguageContextType = {
  language: LanguageCode;
  t: (key: keyof typeof en) => string;
  changeLanguage: (lang: LanguageCode) => void;
};

const translations: Record<LanguageCode, Translations> = {
  en,
  es,
  it,
};

const getInitialLanguage = (): LanguageCode => {
  if (typeof window !== 'undefined') {
    const storedLang = localStorage.getItem('language') as LanguageCode;
    if (storedLang && translations[storedLang]) return storedLang;

    const browserLang = navigator.language.split('-')[0] as LanguageCode;
    return translations[browserLang] ? browserLang : 'en';
  }
  return 'en';
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<LanguageCode>(getInitialLanguage);

  const t = (key: keyof typeof en): string => {
    return translations[language]?.[key] ?? en[key] ?? key;
  };

  const changeLanguage = (lang: LanguageCode) => {
    if (translations[lang]) {
      localStorage.setItem('language', lang);
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
